import AccountPermissions from "@saleor/components/AccountPermissions";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { ShopInfo_shop_permissions } from "@saleor/components/Shop/types/ShopInfo";
import { PermissionGroupErrorFragment } from "@saleor/fragments/types/PermissionGroupErrorFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import PermissionGroup2FA from "@saleor/permissionGroups/components/PermissionGroup2FA/PermissionGroup2FA";
import { MembersListUrlSortField } from "@saleor/permissionGroups/urls";
import {
  extractPermissionCodes,
  isGroupFullAccess
} from "@saleor/permissionGroups/utils/utils";
import { ListActions, SortPage } from "@saleor/types";
import { PermissionEnum } from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getPermissionGroupErrorMessage from "@saleor/utils/errors/permissionGroups";
import React from "react";
import { useIntl } from "react-intl";

import {
  PermissionGroupDetails_permissionGroup,
  PermissionGroupDetails_permissionGroup_users
} from "../../types/PermissionGroupDetails";
import CouponsSubPermissionGroupDialog from "../CouponsSubPermissionGroupDialog";
import PermissionGroupInfo from "../PermissionGroupInfo";
import PermissionGroupMemberList from "../PermissionGroupMemberList";

export interface PermissionGroupDetailsPageFormData {
  name: string;
  hasFullAccess: boolean;
  isActive: boolean;
  permissions: PermissionEnum[];
  users: PermissionGroupDetails_permissionGroup_users[];
}

export interface PermissionData extends ShopInfo_shop_permissions {
  lastSource?: boolean;
  disabled?: boolean;
}

export interface PermissionGroupDetailsPageProps
  extends ListActions,
    SortPage<MembersListUrlSortField> {
  disabled: boolean;
  errors: PermissionGroupErrorFragment[];
  isMfaEnabled: boolean;
  members: PermissionGroupDetails_permissionGroup_users[];
  membersModified: boolean;
  permissionGroup: PermissionGroupDetails_permissionGroup;
  permissions: PermissionData[];
  permissionsExceeded: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onAssign: () => void;
  onBack: () => void;
  onUnassign: (ids: string[]) => void;
  onSubmit: (data: PermissionGroupDetailsPageFormData) => SubmitPromise;
  setIs2FAEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  hasChangesMfa?: boolean;
  handleSwitchChange?: () => void;
}

const PermissionGroupDetailsPage: React.FC<PermissionGroupDetailsPageProps> = ({
  disabled,
  isMfaEnabled,
  errors,
  members,
  membersModified,
  onBack,
  onSubmit,
  permissionGroup,
  permissions,
  permissionsExceeded,
  saveButtonBarState,
  setIs2FAEnabled,
  handleSwitchChange,
  hasChangesMfa,
  ...listProps
}) => {
  const intl = useIntl();
  const initialCouponSubAccessContent = "Current";

  const initialForm: PermissionGroupDetailsPageFormData = {
    hasFullAccess: isGroupFullAccess(permissionGroup, permissions),
    isActive: false,
    name: permissionGroup?.name || "",
    permissions: extractPermissionCodes(permissionGroup),
    users: members
  };

  const formErrors = getFormErrors(["addPermissions"], errors);
  const permissionsError = getPermissionGroupErrorMessage(
    formErrors.addPermissions,
    intl
  );

  return (
    <Form initial={initialForm} onSubmit={onSubmit} confirmLeave>
      {({ change, data, hasChanged, submit }) => {
        const breadcrumbDirectory = [
          {
            name: "User Types",
            url: "/permission-groups"
          },
          {
            name: permissionGroup?.name
          }
        ];

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <div style={{ marginTop: "2px" }}>
                <PageBackNavigation onBack={onBack} />
              </div>
              <PageHeader title={permissionGroup?.name} />
            </div>

            <Grid>
              <div>
                <PermissionGroupInfo
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                />
                <FormSpacer />
                <PermissionGroupMemberList
                  disabled={disabled}
                  {...listProps}
                  users={data?.users || []}
                />
                <FormSpacer />
                {data.permissions.includes(PermissionEnum.MANAGE_COUPONS) && (
                  <CouponsSubPermissionGroupDialog
                    data={data}
                    onChange={change}
                  />
                )}
              </div>
              <div>
                <PermissionGroup2FA
                  data={data}
                  permissionGroup={permissionGroup}
                  isMfaEnabled={isMfaEnabled}
                  handleSwitchChange={handleSwitchChange}
                />
                <FormSpacer />
                <AccountPermissions
                  permissionsExceeded={permissionsExceeded}
                  data={data}
                  disabled={disabled}
                  permissions={permissions}
                  onChange={change}
                  errorMessage={permissionsError}
                  initialCouponSubAccessContent={initialCouponSubAccessContent}
                  fullAccessLabel={intl.formatMessage({
                    defaultMessage: "Group has full access to the store",
                    description: "checkbox label"
                  })}
                  description={intl.formatMessage({
                    defaultMessage:
                      "Expand or restrict group's permissions to access certain part of saleor system.",
                    description: "card description"
                  })}
                />
              </div>
            </Grid>
            <div>
              <SaveButtonBar
                onCancel={onBack}
                onSave={submit}
                state={saveButtonBarState}
                disabled={
                  disabled || !(hasChanged || membersModified || hasChangesMfa)
                }
              />
            </div>
          </Container>
        );
      }}
    </Form>
  );
};
PermissionGroupDetailsPage.displayName = "PermissionGroupDetailsPage";
export default PermissionGroupDetailsPage;
